import { FC, useCallback, useMemo, useState } from 'react'
import { useUpdateEffect } from 'react-use'

import { Media } from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { ProductReviewsContainer } from '../../../../../containers/ProductReviewsContainer'
import { useProductReviewFilters } from '../../../../../lib/hooks/useProductReviewFilters'
import { fieldsByTag } from '../../../../../lib/reviews/constants'
import {
  getTranslatedValueFromReviewsFilter,
  isReviewsTranslatedFilterKey,
  isReviewsTranslatedFilterWithValuesKey,
} from '../../../../../lib/reviews/utils'
import { FilterSortButton } from '../FilterSortButton'

import { ActionsBar } from './ActionsBar'
import { FilterPanel } from './FilterPanel'
import { FiltersList } from './FiltersList'
import { Modal } from './Modal'
import { Container } from './styles'
import { TopBar } from './TopBar'
import { FilterField } from './types'

export const Filters: FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isModalDirty, setIsModalDirty] = useState<boolean>(false)
  const { results } = ProductReviewsContainer.useContainer()
  const { updateFilters, filters, activeFilterCount } = useProductReviewFilters()
  const {
    magnolia: {
      reviewsPage: {
        filters: { filter: filterFixtures },
      },
    },
  } = useTranslation()

  useUpdateEffect(() => {
    setIsModalDirty(true)
  }, [activeFilterCount])

  const toggleFilterValues = useCallback((currentValues: string[], selectedValue: string) => {
    return currentValues.includes(selectedValue)
      ? currentValues.filter(value => value !== selectedValue)
      : [...currentValues, selectedValue]
  }, [])

  const onRatingChange = useCallback(
    (selectedValue: string) => {
      updateFilters({
        ...filters,
        rating: toggleFilterValues(filters.rating ?? [], selectedValue),
      })
    },
    [filters, toggleFilterValues, updateFilters]
  )

  const openModal = () => setIsModalOpen(true)
  const closeModal = () => setIsModalOpen(false)

  const fields = useMemo((): FilterField[] => {
    if (!results?.tag) return []

    return fieldsByTag[results.tag].map(field => {
      const value = filters[field.type] ?? []
      const name = field.name
      const isNameValidReviewKey = isReviewsTranslatedFilterKey(name)
      const isNameValidReviewKeyWithValues = isReviewsTranslatedFilterWithValuesKey(name)

      field.localizedName = isNameValidReviewKey ? filterFixtures[name].title : undefined

      field.values = field.values.map(value => {
        return {
          ...value,
          localizedName:
            (isNameValidReviewKeyWithValues
              ? getTranslatedValueFromReviewsFilter({
                  valueKey: value.name,
                  filter: filterFixtures[name],
                })
              : undefined) ?? value.name,
        }
      })

      const onChange = (selectedValue: string) =>
        updateFilters({ ...filters, [field.type]: toggleFilterValues(value ?? [], selectedValue) })

      return { field, value, onChange }
    })
  }, [filterFixtures, filters, results?.tag, toggleFilterValues, updateFilters])

  if (fields.length < 1) return null

  return (
    <Container>
      <Media greaterThanOrEqual='md'>
        <FilterPanel {...{ fields, onRatingChange }} />
      </Media>

      <Media lessThan='md'>
        <Modal
          isOpen={isModalOpen}
          onModalClose={closeModal}
          onAfterClose={() => setIsModalDirty(false)}
        >
          <TopBar onModalCloseClick={closeModal} />
          <FiltersList {...{ fields, onRatingChange }} />
          {isModalDirty ? <ActionsBar onApplyFilters={closeModal} /> : null}
        </Modal>
        <FilterSortButton onFilterClick={openModal} />
      </Media>
    </Container>
  )
}
